/**
 * Star rating widgets for the customer surveys (tracker item 69).
 *
 * StarDisplay shows a stored rating (the filled stars and a text label that
 * a screen reader reads as "4 out of 5"). StarPicker is the input: a native
 * radio group (arrow keys, one tab stop, form semantics for free) where each
 * choice shows its number and label, so the selection is never conveyed by
 * colour alone, and has a large touch target.
 */
import React, { useId } from 'react';
import { RATINGS, ratingLabel, ratingText } from '../pages/surveys/surveyUi';

export function StarDisplay({ rating, max = 5, className = '' }: { rating: number; max?: number; className?: string }) {
  return (
    <span role="img" aria-label={`${ratingText(rating, max)} stars`} className={`inline-flex whitespace-nowrap text-amber-500 ${className}`}>
      {Array.from({ length: max }, (_, i) => (
        <span key={i} aria-hidden="true">{i < rating ? '★' : '☆'}</span>
      ))}
    </span>
  );
}

interface PickerProps {
  value: number | null;
  onChange: (rating: number) => void;
  labels?: Record<number, string>;
  disabled?: boolean;
  /** Accessible name of the group */
  legend?: string;
}

export function StarPicker({ value, onChange, labels, disabled = false, legend = 'Your rating' }: PickerProps) {
  const name = useId();
  return (
    <fieldset className="border-0 p-0 m-0" disabled={disabled}>
      <legend className="sr-only">{legend}</legend>
      <div role="radiogroup" aria-label={legend} className="grid grid-cols-5 gap-1.5 sm:gap-2">
        {RATINGS.map((n) => {
          const selected = value === n;
          const filled = value !== null && n <= value;
          return (
            <label
              key={n}
              className={`flex min-h-[72px] cursor-pointer select-none flex-col items-center justify-center rounded-lg border-2 px-1 py-2 text-center transition focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-1 ${
                selected ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              <input
                type="radio"
                name={name}
                value={n}
                checked={selected}
                onChange={() => onChange(n)}
                className="sr-only"
                aria-label={`${n} ${ratingLabel(n, labels)}`}
              />
              <span aria-hidden="true" className={`text-3xl leading-none ${filled ? 'text-amber-500' : 'text-gray-300'}`}>{filled ? '★' : '☆'}</span>
              <span aria-hidden="true" className="mt-1 text-sm font-semibold">{n}</span>
              <span aria-hidden="true" className="text-[11px] leading-tight sm:text-xs">{ratingLabel(n, labels)}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
